/**
 * Splash notices (configuration.notices.list → /loans/configuration/notices). LOP-owned.
 *
 * Bill 2026-11-02: a splash on open for planned outages and major releases — shown to everyone using the chosen loans apps until
 * they click "Got it" or the notice ends. Changing the title, message or kind shows it again to people who already dismissed it;
 * moving the times alone does not. "End now" stops showing it immediately. Times are entered and shown in Central time.
 *
 * Layout follows the house editor pattern (src/modules/System/SettingsPage.tsx): a Card with counts in the title and the filter /
 * "+ New notice" in its actions slot, a per-row Edit button, every edit in a ModalShell carrying the change reason, the saving state
 * and the error; a dismissible toast on success.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Banner, Button, Card, Pill, Spinner, type PillTone } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField, TextArea, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { loansApi } from '../loansApi';
import { errorText, fmtCt } from '../writeups/common';
import { centralInputToUtcIso, modalError, nowCentralInput, utcToCentralInput } from '../adminKit';

interface Notice {
  id: number;
  version: number;
  title: string;
  body: string;
  severity: string;
  apps: string;
  startsUtc: string;
  endsUtc: string;
  createdBy: string;
  createdUtc: string;
  updatedBy: string | null;
  updatedUtc: string | null;
}

interface Draft {
  id: number | null;
  title: string;
  body: string;
  severity: string;
  apps: string;
  starts: string;
  ends: string;
}

const SEVERITIES = [
  { value: 'info', label: 'Information' },
  { value: 'warning', label: 'Warning' },
  { value: 'outage', label: 'Outage' },
];

const AUDIENCES = [
  { value: '*', label: 'Every loans app' },
  { value: 'lwp', label: 'Write-up dashboard (SharePoint) only' },
  { value: 'lop', label: 'Loans app in Teams only' },
];

const severityLabel = (s: string): string => SEVERITIES.find((x) => x.value === s)?.label ?? s;
const severityTone = (s: string): PillTone => (s === 'outage' ? 'err' : s === 'warning' ? 'warn' : 'info');
const audienceLabel = (apps: string): string => AUDIENCES.find((a) => a.value === apps)?.label ?? (apps === 'lop,lwp' ? 'Every loans app' : apps);

type Status = 'live' | 'scheduled' | 'ended';
const ms = (iso: string): number => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
function statusOf(n: Notice, now: number): Status {
  if (ms(n.endsUtc) <= now) return 'ended';
  return ms(n.startsUtc) > now ? 'scheduled' : 'live';
}
const statusTone: Record<Status, PillTone> = { live: 'ok', scheduled: 'info', ended: 'muted' };

function emptyDraft(): Draft {
  return { id: null, title: '', body: '', severity: 'info', apps: '*', starts: nowCentralInput(), ends: nowCentralInput(24 * 60) };
}

function toDraft(n: Notice): Draft {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    severity: n.severity,
    apps: AUDIENCES.some((a) => a.value === n.apps) ? n.apps : '*',
    starts: utcToCentralInput(n.startsUtc),
    ends: utcToCentralInput(n.endsUtc),
  };
}

export function SplashNoticesPage(): JSX.Element {
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [ending, setEnding] = useState<Notice | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadError(null);
      try {
        const r = await loansApi.get<{ notices: Notice[] }>('v3/loans/admin/notices');
        if (!cancelled) setRows(r.notices ?? []);
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);
  const now = Date.now();

  const filtered = useMemo(() => {
    if (!rows) return [];
    const n = filter.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) => `${r.title} ${r.body} ${severityLabel(r.severity)} ${audienceLabel(r.apps)}`.toLowerCase().includes(n));
  }, [rows, filter]);

  const openModal = (open: () => void): void => {
    setReason('');
    setTried(false);
    setSaveError(null);
    open();
  };

  const closeModal = (): void => {
    if (saving) return;
    setDraft(null);
    setEnding(null);
    setSaveError(null);
  };

  const startsIso = draft ? centralInputToUtcIso(draft.starts) : null;
  const endsIso = draft ? centralInputToUtcIso(draft.ends) : null;
  const problem = ((): string | null => {
    if (!draft) return null;
    if (!draft.title.trim()) return 'Enter a title.';
    if (draft.title.trim().length > 200) return 'Keep the title to 200 characters.';
    if (!draft.body.trim()) return 'Enter the message people will see.';
    if (draft.body.trim().length > 4000) return 'Keep the message to 4,000 characters.';
    if (!startsIso || !endsIso) return 'Enter when the notice starts and ends.';
    if (ms(endsIso) <= ms(startsIso)) return 'The notice must end after it starts.';
    if (draft.id === null && ms(endsIso) <= Date.now()) return 'The end time is already past.';
    return null;
  })();

  const save = async (): Promise<void> => {
    if (!draft) return;
    setTried(true);
    if (problem) {
      setSaveError(problem);
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = {
        title: draft.title.trim(),
        body: draft.body.trim(),
        severity: draft.severity,
        apps: draft.apps,
        startsUtc: startsIso,
        endsUtc: endsIso,
      };
      if (draft.id === null) {
        await loansApi.send('POST', 'v3/loans/admin/notices', body, asciiReason(reason));
        setToast(`Created the notice "${body.title}".`);
      } else {
        await loansApi.send('POST', `v3/loans/admin/notices/${draft.id}`, body, asciiReason(reason));
        setToast(`Updated the notice "${body.title}".`);
      }
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const endNow = async (): Promise<void> => {
    if (!ending) return;
    setTried(true);
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      await loansApi.send('POST', `v3/loans/admin/notices/${ending.id}/expire`, {}, asciiReason(reason));
      setToast(`Ended the notice "${ending.title}". It is no longer shown.`);
      setEnding(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const columns: ReadonlyArray<DataTableColumn<Notice>> = [
    {
      header: 'Title',
      cell: (n) => (
        <>
          <span className="cell-strong">{n.title}</span>{' '}
          <Pill tone={severityTone(n.severity)}>{severityLabel(n.severity)}</Pill>
          <div className="cell-desc value-clip">{n.body}</div>
        </>
      ),
      sortKey: (n) => n.title,
    },
    { header: 'Shown in', cell: (n) => audienceLabel(n.apps), sortKey: (n) => n.apps },
    { header: 'Starts', cell: (n) => fmtCt(n.startsUtc), sortKey: (n) => n.startsUtc },
    { header: 'Ends', cell: (n) => fmtCt(n.endsUtc), sortKey: (n) => n.endsUtc },
    {
      header: 'Status',
      cell: (n) => {
        const s = statusOf(n, now);
        return <Pill tone={statusTone[s]}>{s}</Pill>;
      },
      sortKey: (n) => ['live', 'scheduled', 'ended'].indexOf(statusOf(n, now)),
    },
    {
      header: 'Changed',
      cell: (n) => (
        <span className="cell-desc">
          {fmtCt(n.updatedUtc ?? n.createdUtc)} by {n.updatedBy ?? n.createdBy}
        </span>
      ),
      sortKey: (n) => n.updatedUtc ?? n.createdUtc,
    },
    {
      header: '',
      cell: (n) =>
        statusOf(n, now) === 'ended' ? (
          <span className="cell-dim">ended</span>
        ) : (
          <div className="card-actions-row">
            <Button onClick={() => openModal(() => setDraft(toDraft(n)))}>Edit</Button>
            <Button onClick={() => openModal(() => setEnding(n))}>End now</Button>
          </div>
        ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load the splash notices: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading the splash notices…" />;

  const live = rows.filter((n) => statusOf(n, now) === 'live').length;
  const editingExisting = draft !== null && draft.id !== null;

  return (
    <div>
      <h2>Splash notices</h2>
      <p className="page-intro">
        A notice opens on top of the loans apps for everyone until they click &ldquo;Got it&rdquo; or the notice ends. Use it for planned
        outages and major releases. Times are Central. Notices that ended more than 30 days ago drop off this list.
      </p>

      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={
          filter.trim()
            ? `Splash notices — ${filtered.length} of ${rows.length}`
            : `Splash notices — ${rows.length}${live ? ` (${live} showing now)` : ''}`
        }
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField label="" value={filter} onChange={setFilter} placeholder="Filter by title, message, kind or app…" />
            </div>
            <Button variant="primary" onClick={() => openModal(() => setDraft(emptyDraft()))}>
              + New notice
            </Button>
          </div>
        }
      >
        <DataTable<Notice>
          columns={columns}
          rows={filtered}
          rowKey={(n) => String(n.id)}
          initialSort={{ columnIndex: 2, dir: 'desc' }}
          emptyState={<p className="empty">{rows.length ? 'No notices match the filter.' : 'There are no notices.'}</p>}
        />
      </Card>

      {/* ── New / edit ─────────────────────────────────────────────────── */}
      <ModalShell
        open={draft !== null}
        title={editingExisting ? 'Edit notice' : 'New notice'}
        onClose={closeModal}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : editingExisting ? 'Save changes' : 'Create notice'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <TextField label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} maxLength={200} required />
            <TextArea
              label="Message"
              value={draft.body}
              onChange={(v) => setDraft({ ...draft, body: v })}
              rows={5}
              required
              hint={
                editingExisting
                  ? 'Changing the title, message or kind shows the notice again to people who already clicked "Got it".'
                  : 'What people will read. Plain text.'
              }
            />
            <SelectField label="Kind" value={draft.severity} onChange={(v) => setDraft({ ...draft, severity: v })} options={SEVERITIES} />
            <SelectField label="Shown in" value={draft.apps} onChange={(v) => setDraft({ ...draft, apps: v })} options={AUDIENCES} />
            <label className="form-field">
              <span className="form-field__label">
                Starts (Central)<span className="form-field__required" aria-hidden> *</span>
              </span>
              <input
                type="datetime-local"
                className="form-input"
                value={draft.starts}
                onChange={(e) => setDraft({ ...draft, starts: e.target.value })}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">
                Ends (Central)<span className="form-field__required" aria-hidden> *</span>
              </span>
              <input
                type="datetime-local"
                className="form-input"
                value={draft.ends}
                onChange={(e) => setDraft({ ...draft, ends: e.target.value })}
              />
              <span className="form-field__hint">The notice stops showing at this time.</span>
            </label>
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>

      {/* ── End now ────────────────────────────────────────────────────── */}
      <ModalShell
        open={ending !== null}
        title="End this notice now?"
        onClose={closeModal}
        width={560}
        actions={
          ending && (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void endNow()} disabled={saving}>
                {saving ? 'Ending…' : 'End now'}
              </Button>
            </>
          )
        }
      >
        {ending && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p>
              <strong>{ending.title}</strong> stops showing immediately, in every app it was shown in. It stays on this list for 30
              days.
            </p>
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>
    </div>
  );
}
